export type ContestType = 'district' | 'statewide' | 'chamber'

export interface ContestRequest {
  type: ContestType
  office: string
  state: string
  districtGeoUnitId?: string | null
  chamber?: 'stateHouse' | 'stateSenate' | null
  vintage?: number | null
  raceKind?: 'general' | 'primary'
}

export type SimBasis = 'returns' | 'topOfTicket' | 'none' | 'primary'

export interface SimCoverage {
  modeledFromReturns: number
  modeledFromTopOfTicket: number
  insufficient: number
  basis: SimBasis
  refusalReason?: string | null
}

export interface MarginBand {
  p10: number
  p50: number
  p90: number
}

export interface SimBaselineResponse {
  coverage: SimCoverage
  winProbability: number | null
  marginVotes: MarginBand | null
  marginPp: MarginBand | null
  baselineContestsUsed: string[]
  unitCount: number
}

export interface CapacityPlan {
  canvassers: number
  shiftsPerWeek: number
  weeks: number
  doorsPerShift: number
  contactRatePct: number
}

export interface Assumptions {
  turnoutLiftPp: number
  persuasionPp: number
}

export interface AdvancedLevers {
  turnoutDeltaPp?: number | null
  persuasionDeltaPp?: number | null
}

export type TargetingKind = 'geography' | 'precincts' | 'turf' | 'list' | 'flags' | 'smartfilter'

export interface SmartFilter {
  propensityLo: number
  propensityHi: number
  leanThreshold: number
  persuasionMidband: boolean
  registeredDemOnly?: boolean
  laneDirection?: 'progressive' | 'moderate'
  issues?: string[]
  activismFloor?: number | null
}

export interface Targeting {
  kind: TargetingKind
  precinctGeoUnitIds?: string[]
  turfId?: string | null
  listId?: string | null
  flagKeys?: string[]
  smartFilter?: SmartFilter | null
}

export const SMART_FILTER_DEFAULTS: SmartFilter = {
  propensityLo: 30,
  propensityHi: 60,
  leanThreshold: 50,
  persuasionMidband: false,
}

/** Issue-alignment synthetics available as primary targeting toggles (key -> label). */
export const PRIMARY_ISSUES: { key: string; label: string }[] = [
  { key: 'proChoice', label: 'Abortion rights' },
  { key: 'proClimate', label: 'Climate' },
  { key: 'proGunControl', label: 'Gun control' },
]

export const PRIMARY_SMART_FILTER_DEFAULTS: SmartFilter = {
  propensityLo: 40,
  propensityHi: 100,
  leanThreshold: 50,
  persuasionMidband: false,
  registeredDemOnly: true,
  laneDirection: 'progressive',
  issues: [],
  activismFloor: null,
}

export interface SimRunRequest {
  contest: ContestRequest
  capacity: CapacityPlan
  targeting: Targeting
  assumptions: Assumptions
  advanced?: AdvancedLevers | null
  seatShiftAllocation?: Record<string, number> | null
  scenarioId?: string | null
  compareUntargeted?: boolean
}

export interface SimTargetingCountRequest {
  contest: ContestRequest
  targeting: Targeting
}

export interface SimTargetingCount {
  reachable: number
}

export interface TornadoRow {
  geoUnitId: string
  unitName: string
  marginalNetDemVotesPer100Shifts: number
}

export interface SeatResult {
  seatCode: string
  seatName: string
  geoUnitId: string
  winProbability: number
  baselineWinProbability: number
  coverage: 'Modeled' | 'LeanOnly' | 'Insufficient'
  marginalNetDemVotesPer100Shifts: number
}

export interface ChamberResult {
  majorityProbability: number
  baselineMajorityProbability: number
  medianSeats: number
  majorityThreshold: number
  fixedDemSeats: number
  fixedRepSeats: number
  excludedSeats: number
  seatsHistogram: number[]
  histogramMinSeats: number
  seats: SeatResult[]
}

export interface UntargetedComparison {
  netVotesMoved: number
  totalContacts: number
  totalReachableVoters: number
  efficiencyNetPer1k: number
}

export interface SimRunResponse {
  coverage: SimCoverage
  winProbability: number | null
  baselineWinProbability: number | null
  marginVotes: MarginBand | null
  marginPp: MarginBand | null
  baselineMarginVotes: MarginBand | null
  votesNeeded: number | null
  netVotesMoved: number | null
  turnoutVotersAdded: number | null
  alignedVotersAdded: number | null
  opposingVotersAdded: number | null
  persuadedVotersAdded: number | null
  advancedVotesMoved: number | null
  totalContacts: number
  totalShifts: number
  totalReachableVoters: number
  tornado: TornadoRow[]
  unitSensitivities: { geoUnitId: string; value: number }[]
  chamber: ChamberResult | null
  staleRefs: string[]
  baselineContestsUsed: string[]
  efficiencyNetPer1k?: number | null
  untargetedComparison?: UntargetedComparison | null
}

export interface ScenarioSummary {
  id: string
  name: string
  contestJson: string
  lastRunSummaryJson: string | null
  pinnedGeoUnitId: string | null
  updatedAt: string
}

export interface ScenarioDetail extends ScenarioSummary {
  leversJson: string
  createdAt: string
}

export interface ScenarioSave {
  name: string
  contestJson: string
  leversJson: string
}

export const CAPACITY_DEFAULTS: CapacityPlan = {
  canvassers: 20,
  shiftsPerWeek: 3,
  weeks: 8,
  doorsPerShift: 30,
  contactRatePct: 20,
}

export const ASSUMPTION_DEFAULTS: Assumptions = {
  turnoutLiftPp: 8,
  persuasionPp: 0,
}
